import React, { useEffect, useState } from "react";
import { SortArrowDownIcon } from "../../../assets/icons";
import { useNotification } from "../../toster-notification/NotificationContext";
import { useNavigate, useParams } from "react-router-dom";
import { LocalInitClientDTO } from "../../../models/local/local.response";
import { localService } from "../../../services/localService";
import Loader from "../../shared/Loader";
import { ProductCardStyleType } from "../../../models/base/ProductCardStyleType";
import LocalInfoView from "../shared/LocalInfoView";
import ClientOrdersView from "../shared/ClientOrdersView";
import CategoryTypes from "./CategoryTypes";
import SubcategoryDescription from "./SubcategoryDescription";
import ProductCardType1 from "../../shared/products cards/ProductCardType1";
import ProductCardType2 from "../../shared/products cards/ProductCardType2";
import { useAuth } from "../../../auth/AuthContext";
import { handleErrorNotification } from "../../../utils/errorNotificatioMessage";
import ReservationView from "../shared/ReservationView";
import BasketView from "./views/BasketView";
import Notifications from "../shared/Notifications";
import { useProductFilter } from "../../../hooks/useProductFilter";
import { useCart } from "../../../hooks/useCart";
import { AnimatePresence } from "framer-motion";
import { OrderStatusType, orderStatusTypeToString } from "../../../models/base/OrderStatusType";
import { connection, startConnection } from "../../../services/signalRConnection";
import ClientMenuButtons from "../shared/ClientMenuButtons";
import ClientMenuHeader from "../shared/ClientMenuHeader";
import FiltersView from "../shared/FiltersView";
import ProductCardType3 from "../../shared/products cards/ProductCardType3";
import ProductCardType4 from "../../shared/products cards/ProductCardType4";
import LocalEventView from "../shared/LocalEventView";

const OnlineClientPage: React.FC = () => {

    const [isLocalInfoViewOpen, setIsLocalInfoViewOpen] = useState(false);
    const [isClientOrdersViewOpen, setIsClientOrdersViewOpen] = useState(false);
    const [isBasketViewOpen, setIsBasketViewOpen] = useState(false);
    const [isReservationViewOpen, setIsReservationViewOpen] = useState(false);
    const [isFiltersViewOpen, setIsFiltersViewOpen] = useState(false);

    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>("");

    const [localData, setLocalData] = useState<LocalInitClientDTO>();
    const [loading, setLoading] = useState<boolean>(false);

    const { notify } = useNotification();
    const { localId } = useParams<{ localId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();

    const cart = useCart();
    const productFilter = useProductFilter(localData, selectedCategoryId);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    useEffect(() => {
        fetchMenu();
    }, [localId]);

    useEffect(() => {

        const handler = (orderId: string, status: OrderStatusType) => {
            notify("info", `Porudžbina #${orderId.slice(0, 8)} je promenila status na ${orderStatusTypeToString(status)}`, "Novi status porudžbine")
        }

        const setup = async () => {
            await startConnection();
            connection.on("OrderStatusChange", handler);
        }

        setup();

        return () => {
            connection.off("OrderStatusChange", handler);
        };

    }, [localId]);


    // Scroll spy za podkategorije
    useEffect(() => {
        if (productFilter.filters.allItems) return;

        const observerOptions = {
            root: null,
            rootMargin: "-160px 0px -60% 0px", // Podesi margine u zavisnosti od visine navigacije
            threshold: 0
        };

        const observerCallback: IntersectionObserverCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const subcatId = entry.target.getAttribute("data-subcategory-id");
                    if (subcatId && subcatId !== selectedSubcategoryId) {
                        setSelectedSubcategoryId(subcatId);
                    }
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);
        const subcategorySections = document.querySelectorAll(".subcategory-section-wrapper");

        subcategorySections.forEach((section) => observer.observe(section));

        return () => {
            subcategorySections.forEach((section) => observer.unobserve(section));
            observer.disconnect();
        };
    }, [productFilter.displaySubcategories, selectedSubcategoryId, productFilter.filters.allItems]);

    useEffect(() => {
        if (localData?.theme?.fontFamily) {
            fetchFont(localData.theme.fontFamily);
        }
    }, [localData?.theme?.fontFamily]);

    const fetchMenu = async () => {

        if (!localId)
            return;

        setLoading(true);
        try {
            const response = await localService.getInitClientMenu(localId);

            if (response.data && response.success) {
                setLocalData(response.data);

                if (response.data.categories.length > 0) {
                    const firstOrderCategory = response.data.categories.sort((a, b) => a.order - b.order)[0];
                    setSelectedCategoryId(firstOrderCategory.id);

                    const firstOrderSubCategory = firstOrderCategory.subcategories.sort((a, b) => a.order - b.order)[0];
                    setSelectedSubcategoryId(firstOrderSubCategory.id);
                }
            } else {
                notify("error", "Grška prilikom dobavljanja podataka lokala");
            }
        } catch (error: any) {
            notify("error", "Grška prilikom dobavljanja podataka lokala");
            handleErrorNotification(error, notify);
        } finally {
            setLoading(false);
        }
    }

    const fetchFont = (fontFamily: string) => {
        if (!fontFamily)
            return;

        const font = fontFamily.replaceAll(" ", "+");
        const className = "client-main-layout-section";
        const linkId = `font-${font}`;

        const layout = document.getElementsByClassName(className)[0] as HTMLElement | undefined;

        if (!layout)
            return;

        const existingLink = document.getElementById(linkId);

        if (!existingLink) {
            const link = document.createElement("link");
            link.id = linkId;
            link.rel = "stylesheet";
            link.href = `https://fonts.googleapis.com/css2?family=${font}:wght@300;400;500;600;700&display=swap`;

            document.head.appendChild(link);
        }

        layout.style.fontFamily = `"${fontFamily}", sans-serif`;
    };


    if (loading) return <Loader />;
    if (!localId) return null;
    if (!localData) return <div>Local data missing</div>;

    return (
        <>

            <AnimatePresence>
                {localData.localEvents.length > 0 && localData.localEvents.map(localEvent => (
                    <LocalEventView
                        key={localEvent.id}
                        localEvent={localEvent}
                        onClose={() => setLocalData(prev => {
                            if (!prev) return undefined;

                            return {
                                ...prev,
                                localEvents: prev.localEvents.filter((e) => e.id !== localEvent.id),
                            } as LocalInitClientDTO;
                        })}
                    />
                ))}
            </AnimatePresence>

            <AnimatePresence>
                {isLocalInfoViewOpen &&
                    <LocalInfoView
                        latitude={localData.latitude}
                        longitude={localData.longitude}
                        name={localData.name}
                        description={localData.description}
                        logoImageUrl={localData.logoImageUrl}
                        backgroundImageUrl={localData.backgroundImageUrl}
                        email={localData.email}
                        address={localData.address}
                        phoneNumber={localData.phoneNumber}
                        website={localData.website}
                        workingHours={localData.workingHours}
                        wiFiName={localData.wiFiName}
                        wiFiPassword={localData.wiFiPassword}
                        socialLinks={localData.socialLinks}
                        onClose={() => setIsLocalInfoViewOpen(false)}
                    />
                }
            </AnimatePresence>

            {isClientOrdersViewOpen &&
                <ClientOrdersView
                    localName={localData.name}
                    onClose={() => setIsClientOrdersViewOpen(false)}
                />
            }

            <AnimatePresence>
                {isBasketViewOpen &&
                    <BasketView
                        onClose={() => setIsBasketViewOpen(false)}
                        cart={cart}
                        primaryColor={localData.theme.primaryColor}
                        planSubscriptionFeatures={localData.planSubscriptionFeatures}
                    />
                }
            </AnimatePresence>

            {isReservationViewOpen &&
                <ReservationView
                    primaryColor={localData.theme.primaryColor}
                    onClose={() => setIsReservationViewOpen(false)}
                />
            }

            <AnimatePresence>
                {isFiltersViewOpen &&
                    <FiltersView
                        filter={productFilter.filters}
                        setFilters={productFilter.setFilters}
                        onClose={() => setIsFiltersViewOpen(false)}
                        primaryColor={localData.theme.primaryColor}

                    />
                }
            </AnimatePresence>

            <Notifications
                notifications={localData.notifications}
            />

            <div className="client-main-layout-section" style={{ fontFamily: localData.theme.fontFamily }}>

                {/* ----- HEADER ----- */}
                <ClientMenuHeader
                    user={user}
                    localId={localId}
                    navigate={navigate}
                    logoImageUrl={localData.logoImageUrl}
                    backgroundImageUrl={localData.backgroundImageUrl}
                    localName={localData.name}
                    localAddress={localData.address}
                    setIsReservationViewOpen={setIsReservationViewOpen}
                    setIsClientOrdersViewOpen={setIsClientOrdersViewOpen}
                    setIsLocalInfoViewOpen={setIsLocalInfoViewOpen}
                    planSubscriptionFeatures={localData.planSubscriptionFeatures}
                    primaryColor={localData.theme.primaryColor}
                />


                {/* ----- KATEGORIJE I PODKATEGORIJE ----- */}
                {!productFilter.filters.allItems ? (
                    <div className="category-subcategory-layout">
                        <CategoryTypes
                            style={localData.theme.categoryStyle}
                            primaryColor={localData.theme.primaryColor}
                            items={localData.categories}
                            selectedItemId={selectedCategoryId}
                            onItemSelect={(id: string) => {
                                setSelectedCategoryId(id);
                                setSelectedSubcategoryId(() => {
                                    const category = localData.categories.find(c => c.id === id);
                                    const firstSubcategory = category?.subcategories.sort((a, b) => a.order - b.order)[0];
                                    if (category && category.subcategories.length > 0) {
                                        return firstSubcategory?.id || "";
                                    }
                                    return "";
                                });
                            }}
                        />
                        <CategoryTypes
                            style={localData.theme.subcategoryStyle}
                            primaryColor={localData.theme.primaryColor}
                            items={localData.categories.find(c => c.id === selectedCategoryId)?.subcategories || []}
                            selectedItemId={selectedSubcategoryId}
                            onItemSelect={(id: string) => {
                                setSelectedSubcategoryId(id);
                                const element = document.getElementById(`subcategory-${id}`);
                                if (element) {
                                    const yOffset = -150; // offset za navbar/header (prilagodi po potrebi)
                                    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                    window.scrollTo({ top: y, behavior: 'smooth' });
                                }
                            }}
                        />
                    </div>
                ) : (
                    <div className="all-items-filter-div">
                        <div className="w-layout-blockcontainer container w-container">
                            <div className="text-muted">Trenutno su prikazani svi proizvodi iz menija</div>
                        </div>
                    </div>
                )}

                {/* ----- PROIZVODI ----- */}
                <section className="client-product-section">
                    <div className="w-layout-blockcontainer container w-container">
                        <div className="client-product-wrapper">

                            <div className="client-products-count-and-sort-div">
                                <div className="client-product-list-header-count-div">
                                    <div className="text-muted">{productFilter.productCount} proizvoda</div>
                                </div>
                                <div className="client-product-list-header-sort-div">
                                    <select
                                        className="form-select white-bg w-select"
                                        onChange={(e) => productFilter.setSortOption(e.target.value)}
                                        value={productFilter.sortOption}
                                    >
                                        <option value="">Sortiraj po...</option>
                                        <option value="price-low">Cena rastuće</option>
                                        <option value="price-high">Cena opadajuće</option>
                                        <option value="newest">Novi proizvodi</option>
                                    </select>

                                    <img
                                        src={SortArrowDownIcon}
                                        alt=""
                                        className="icon-size-28 sort-icon"
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    />
                                    {isDropdownOpen && (
                                        <section className="modal-section filter-products">
                                            <div className="w-layout-blockcontainer container w-container">
                                                <div className="modal-wrapper" onClick={() => setIsDropdownOpen(false)}>
                                                    <div className="modal-div custom-select" onClick={(e) => e.stopPropagation()}>
                                                        <div className="select-list-div">
                                                            <div className="select-div top-div"
                                                                onClick={() => {
                                                                    productFilter.setSortOption("");
                                                                    setIsDropdownOpen(false);
                                                                }}
                                                            >
                                                                <div>Sortiraj po...</div>
                                                            </div>
                                                            {[
                                                                { value: "price-low", label: "Cena rastuće" },
                                                                { value: "price-high", label: "Cena opadajuće" },
                                                                { value: "newest", label: "Novi proizvodi" },
                                                            ].map(option => (
                                                                <div
                                                                    key={option.value}
                                                                    className={`select-div ${productFilter.sortOption === option.value ? "selected" : ""}`}
                                                                    onClick={() => {
                                                                        productFilter.setSortOption(option.value);
                                                                        setIsDropdownOpen(false);
                                                                    }}
                                                                >
                                                                    <div>{option.label}</div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                    )}
                                </div>
                            </div>

                            {productFilter.filters.allItems ? (
                                <>
                                    {/* Flat list za sve proizvode */}
                                    {localData.theme.productCardStyle === ProductCardStyleType.Style_1 && (
                                        <div className="client-product-list-div">
                                            {productFilter.displayProducts.map(product => (
                                                <ProductCardType1 key={product.id}
                                                    primaryColor={localData.theme.primaryColor}
                                                    tagTypes={localData.theme.tagStyle}
                                                    product={product} showAddToCartButton={localData.localSettings.showAddToCartButton} cart={cart} />
                                            ))}
                                        </div>
                                    )}
                                    {localData.theme.productCardStyle === ProductCardStyleType.Style_2 && (
                                        <div className="client-product-list-div prod-style-2">
                                            {productFilter.displayProducts.map(product => (
                                                <ProductCardType2 key={product.id} primaryColor={localData.theme.primaryColor} tagTypes={localData.theme.tagStyle} product={product} showAddToCartButton={localData.localSettings.showAddToCartButton} cart={cart} />
                                            ))}
                                        </div>
                                    )}
                                    {localData.theme.productCardStyle === ProductCardStyleType.Style_3 && (
                                        <div className="client-product-list-div prod-style-3">
                                            {productFilter.displayProducts.map(product => (
                                                <ProductCardType3 key={product.id} primaryColor={localData.theme.primaryColor} tagTypes={localData.theme.tagStyle} product={product} showAddToCartButton={localData.localSettings.showAddToCartButton} cart={cart} />
                                            ))}
                                        </div>
                                    )}
                                    {localData.theme.productCardStyle === ProductCardStyleType.Style_4 && (
                                        <div className="client-product-list-div prod-style-4">
                                            {productFilter.displayProducts.map(product => (
                                                <ProductCardType4 key={product.id} primaryColor={localData.theme.primaryColor} tagTypes={localData.theme.tagStyle} product={product} showAddToCartButton={localData.localSettings.showAddToCartButton} cart={cart} />
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    {/* Prikaz po podkategorijama u odabranoj kategoriji */}
                                    {productFilter.displaySubcategories.map(subcategory => (
                                        <div key={subcategory.id} id={`subcategory-${subcategory.id}`} className="subcategory-section-wrapper" data-subcategory-id={subcategory.id} style={{ marginBottom: "40px" }}>

                                            <div className="client-product-list-subcategory-name">
                                                <div className="client-product-list-subcategory-name-hr left"></div>
                                                <div className="client-product-list-subcategory-name-text">
                                                    {subcategory.name}
                                                </div>
                                                <div className="client-product-list-subcategory-name-hr"></div>
                                            </div>

                                            {subcategory.description && (
                                                <SubcategoryDescription
                                                    description={subcategory.description}
                                                    primaryColor={localData.theme.primaryColor}
                                                />
                                            )}

                                            {localData.theme.productCardStyle === ProductCardStyleType.Style_1 && (
                                                <div className="client-product-list-div">
                                                    {subcategory.products.map(product => (
                                                        <ProductCardType1
                                                            key={product.id}
                                                            primaryColor={localData.theme.primaryColor}
                                                            tagTypes={localData.theme.tagStyle}
                                                            product={product}
                                                            showAddToCartButton={localData.localSettings.showAddToCartButton}
                                                            cart={cart} />
                                                    ))}
                                                </div>
                                            )}
                                            {localData.theme.productCardStyle === ProductCardStyleType.Style_2 && (
                                                <div className="client-product-list-div prod-style-2">
                                                    {subcategory.products.map(product => (
                                                        <ProductCardType2
                                                            key={product.id}
                                                            primaryColor={localData.theme.primaryColor}
                                                            tagTypes={localData.theme.tagStyle}
                                                            product={product}
                                                            showAddToCartButton={localData.localSettings.showAddToCartButton}
                                                            cart={cart} />
                                                    ))}
                                                </div>
                                            )}
                                            {localData.theme.productCardStyle === ProductCardStyleType.Style_3 && (
                                                <div className="client-product-list-div prod-style-3">
                                                    {subcategory.products.map(product => (
                                                        <ProductCardType3
                                                            key={product.id}
                                                            primaryColor={localData.theme.primaryColor}
                                                            tagTypes={localData.theme.tagStyle}
                                                            product={product}
                                                            showAddToCartButton={localData.localSettings.showAddToCartButton}
                                                            cart={cart} />
                                                    ))}
                                                </div>
                                            )}
                                            {localData.theme.productCardStyle === ProductCardStyleType.Style_4 && (
                                                <div className="client-product-list-div prod-style-4">
                                                    {subcategory.products.map(product => (
                                                        <ProductCardType4
                                                            key={product.id}
                                                            primaryColor={localData.theme.primaryColor}
                                                            tagTypes={localData.theme.tagStyle}
                                                            product={product}
                                                            showAddToCartButton={localData.localSettings.showAddToCartButton}
                                                            cart={cart} />
                                                    ))}
                                                </div>
                                            )}

                                        </div>
                                    ))}
                                </>
                            )}

                        </div>
                    </div>
                </section>

                {/* ----- DONJI DUGMIĆI ----- */}
                <ClientMenuButtons
                    setIsFiltersViewOpen={setIsFiltersViewOpen}
                    setIsBasketViewOpen={setIsBasketViewOpen}
                    planSubscriptionFeatures={localData.planSubscriptionFeatures}
                    primaryColor={localData.theme.primaryColor}
                    cartProductsCount={cart.cartProducts.reduce((total, cp) => total + cp.quantity, 0)}
                    onlineMenu={false}
                    localSettings={localData.localSettings}
                    setIsClientOrdersViewOpen={setIsClientOrdersViewOpen}
                    setIsLocalInfoViewOpen={setIsLocalInfoViewOpen}
                    setIsReservationViewOpen={setIsReservationViewOpen}
                    isUserLoggedIn={user !== null}
                />


            </div>

        </>
    );
};

export default OnlineClientPage;